import { hostWarnings } from "./hostWarnings";
import { FastTrackScout } from "./model/fastTrack";
import { Observation } from "./model/observation";
import { Scout, ScoutState } from "./model/scout";
import {
  DEFAULT_OFFLINE_THRESHOLDS,
  ResolvedOfflineThresholds,
  countsAsListening,
  getScoutStateDescription,
  getStateFor,
} from "./scoutState";

/** Flat view of a scout for the devices table and the device page. Keys are plain so Table can sort and search them. */
export interface DeviceRow {
  deviceId: string;
  name: string;
  /** "Pixel 6a · v2.14.0" */
  deviceLine: string;
  /** Derived with the given thresholds, so it can differ from the scout's stored state. */
  state: ScoutState;
  stateLabel: string;
  batteryPercent: number;
  charging: boolean;
  /** Ambient noise in dBfs (0 is full scale, -96 silence); undefined when the scout reports no audio statistics. */
  noiseDbfs: number | undefined;
  eventsPerMinute: number;
  /** Raw tut timestamp of the last status update. */
  lastUpdate: number;
  overridden: boolean;
  overrides: Override[];
  fastTrack: FastTrackScout | undefined;
  /** Host warnings the device reported (JVM scouts only); empty when healthy or not a JVM scout. */
  hostWarnings: string[];
  /** Searchable summary of the Flags column: every override badge, the fast-track flag and the host warnings. */
  flags: string;
  scout: Scout;
}

export interface DeviceCounts {
  total: number;
  listening: number;
  /** Still starting up (commander's "Synchronizing"); not yet listening, not muted. */
  synchronizing: number;
  muted: number;
  offline: number;
}

const isSet = (value: unknown) => value !== undefined && value !== null;

/** One overridden setting, as the Override column shows it: a short name and, when it fits in a badge, the value. */
export interface Override {
  label: string;
  value?: string;
}

interface OverrideField {
  label: string;
  read: (scout: Scout) => unknown;
  /** Numbers and the time service are short enough to show; model and detector ids are hashes. */
  withValue: boolean;
}

// Same settings, same order and short forms of the same names as the device page's override configuration,
// which follows Commander's admin overlay.
const OVERRIDE_FIELDS: OverrideField[] = [
  { label: "Gunshot", read: (s) => s.predictionThreshold, withValue: true },
  { label: "Timing", read: (s) => s.timingThreshold, withValue: true },
  { label: "Krazy8", read: (s) => s.model, withValue: false },
  { label: "Gale", read: (s) => s.detectorId, withValue: false },
  { label: "Gain", read: (s) => s.gain, withValue: true },
  { label: "Time", read: (s) => s.timeServiceName, withValue: true },
];

/** The scout's overridden settings, in the device page's order. */
export const overridesFor = (scout: Scout): Override[] =>
  OVERRIDE_FIELDS.filter((field) => isSet(field.read(scout))).map((field) => ({
    label: field.label,
    value: field.withValue ? String(field.read(scout)) : undefined,
  }));

export const overrideText = (override: Override) => (override.value === undefined ? override.label : `${override.label} ${override.value}`);

/** True when any detection setting is overridden for the scout (the same fields the device page edits). */
export const hasOverrides = (scout: Scout) => overridesFor(scout).length > 0;

export const noiseLevel = (scout: Scout): number | undefined => {
  const averages = scout.status.audioStatistics?.sampleAverages;
  return averages && averages.length > 0 ? averages[0].sampleAverage : undefined;
};

/** dBfs runs from -96 (silence) to 0; shift it into 0..96 for a meter, and invert it for the green-to-red colour scale. */
export const NOISE_RANGE_DBFS = 96;
export const noiseMeterValue = (dbfs: number) => dbfs + NOISE_RANGE_DBFS;
export const noisePercentForColor = (dbfs: number) => 100 - noiseMeterValue(dbfs);

/** Scout documents without a status carry nothing to show; the listener still stores them. */
export const hasStatus = (scout: Scout) => scout.status !== undefined && scout.status !== null;

export const deviceLine = (scout: Scout) =>
  `${scout.status.deviceInfo?.model ?? "Unknown device"} · v${scout.status.appVersion ?? "?"}`;

const flagsText = (overrides: Override[], fastTrack: FastTrackScout | undefined, warnings: string[]) =>
  [
    ...overrides.map(overrideText),
    fastTrack ? `Fast track ${fastTrack.group}${fastTrack.paused ? " Paused" : ""}` : "",
    ...warnings,
  ]
    .filter(Boolean)
    .join(" ");

export const buildDeviceRows = (
  scouts: Scout[],
  observations: { [deviceId: string]: Observation[] },
  fastTrackScouts: FastTrackScout[],
  thresholds: ResolvedOfflineThresholds = DEFAULT_OFFLINE_THRESHOLDS
): DeviceRow[] =>
  scouts.filter(hasStatus).map((scout) => {
    const deviceId = scout.status.deviceId;
    const state = getStateFor(scout, thresholds);
    const overrides = overridesFor(scout);
    const warnings = hostWarnings(scout);
    const overridden = overrides.length > 0;
    const fastTrack = fastTrackScouts.find((f) => f.deviceId === deviceId);
    return {
      deviceId,
      name: scout.status.displayName,
      deviceLine: deviceLine(scout),
      state,
      stateLabel: getScoutStateDescription(scout, state),
      batteryPercent: scout.status.batteryPercent,
      charging: !!(scout.status.usbCharge || scout.status.acCharge),
      noiseDbfs: noiseLevel(scout),
      eventsPerMinute: observations[scout.deviceId]?.length ?? 0,
      lastUpdate: scout.status.dateTime,
      overridden,
      overrides,
      fastTrack,
      hostWarnings: warnings,
      flags: flagsText(overrides, fastTrack, warnings),
      scout,
    };
  });

/**
 * Counts follow commander: an ignored scout (silent past the hide threshold, or hidden by config)
 * is neither listed nor counted, so "offline" means recently silent, not long gone.
 */
export const countDevices = (
  scouts: Scout[],
  thresholds: ResolvedOfflineThresholds = DEFAULT_OFFLINE_THRESHOLDS
): DeviceCounts => {
  const states = scouts
    .filter(hasStatus)
    .map((scout) => getStateFor(scout, thresholds))
    .filter((state) => state !== ScoutState.OfflineIgnore);
  return {
    total: states.length,
    listening: states.filter(countsAsListening).length,
    synchronizing: states.filter((state) => state === ScoutState.Initializing).length,
    muted: states.filter((state) => state === ScoutState.Ready).length,
    offline: states.filter((state) => state === ScoutState.Offline).length,
  };
};

/** The first UUID block (8 characters) for tables; the full ID is what gets copied. */
export const shortDeviceId = (deviceId: string) => deviceId.slice(0, 8);
